/**
 * Papel por sub-sistema (ficha 2026-10-28 `FUN_papel-por-sub-sistema`, lote val1). O papel é uma árvore sub-sistema >
 * página > ação:
 *  - a AÇÃO continua sendo a permissão por módulo que já existe (`<modulo>.ver`, `.editar`...);
 *  - a PÁGINA é a aba de um módulo (`<slug>.<aba>`, a mesma chave dos sub-sistemas, `chavePagina`). O papel guarda as
 *    páginas que DESMARCOU (`papel_paginas_negadas`): o módulo coberto pelo papel dá todas as páginas dele, menos estas.
 *    Guardar a negação mantém os papéis que já existem como estavam (sem linha nenhuma, todas as páginas) e faz a página
 *    nova de um módulo aparecer para quem já usa o módulo;
 *  - o SUB-SISTEMA é só o agrupamento da árvore no editor (marcar dá as ações dos módulos das páginas dele e as páginas).
 *    O papel não guarda o sub-sistema: mudar a divisão (Admin > Configurações > Sub-sistemas) não muda papel nenhum.
 *
 * Papéis somados: a pessoa com mais de um papel tem a soma. A página fica negada só quando TODO papel que cobre o módulo
 * dela a desmarcou (outro papel que cobre o módulo sem desmarcar a devolve). Exceção por pessoa (`membro_ajustes_acesso`):
 * conceder ou retirar uma ação ou uma página por cima da soma dos papéis.
 *
 * Tudo aqui é puro (API, web e testes usam a mesma regra).
 */

export type TipoAjuste = 'permissao' | 'pagina';

export interface AjusteAcesso {
  tipo: TipoAjuste;
  codigo: string;
  /** true: concede por cima dos papéis; false: retira. */
  concede: boolean;
}

/** O que um papel dá, para a soma: as permissões efetivas e as páginas desmarcadas. */
export interface AcessoDoPapel {
  permissoes: ReadonlySet<string>;
  negadas: ReadonlySet<string> | readonly string[];
}

export const RE_PAGINA = /^[a-z][a-z0-9-]{0,39}\.[a-z][a-z0-9-]{0,39}$/;
export const moduloDaPagina = (pagina: string) => pagina.slice(0, pagina.indexOf('.'));

/** O conjunto de permissões cobre o módulo (alguma permissão `<modulo>.x`, ou `*`)? */
export function cobreModulo(permissoes: ReadonlySet<string>, modulo: string): boolean {
  if (permissoes.has('*')) return true;
  const prefixo = `${modulo}.`;
  for (const p of permissoes) if (p.startsWith(prefixo)) return true;
  return false;
}

/**
 * Páginas negadas da soma dos papéis: a página desmarcada em algum papel fica negada se nenhum OUTRO papel que cobre o
 * módulo dela a deixa marcada. Página que nenhum papel desmarcou nunca entra (o módulo dá todas).
 */
export function paginasNegadasDosPapeis(papeis: readonly AcessoDoPapel[]): Set<string> {
  const negadasPorPapel = papeis.map((p) => (p.negadas instanceof Set ? (p.negadas as ReadonlySet<string>) : new Set(p.negadas as readonly string[])));
  const candidatas = new Set<string>();
  for (const n of negadasPorPapel) for (const x of n) candidatas.add(x);
  const saida = new Set<string>();
  for (const pagina of candidatas) {
    const modulo = moduloDaPagina(pagina);
    const liberada = papeis.some((p, i) => cobreModulo(p.permissoes, modulo) && !negadasPorPapel[i]!.has(pagina));
    if (!liberada) saida.add(pagina);
  }
  return saida;
}

/**
 * Acesso efetivo: permissões e páginas negadas da soma dos papéis, com a exceção da pessoa por cima. A permissão concedida
 * pela exceção entra; a retirada sai (mesmo vinda de vários papéis). A página concedida sai das negadas; a retirada entra.
 * Dono (`*`) não recebe exceção (a regra de quem grava recusa; aqui, por garantia, o `*` fica como está).
 */
export function acessoEfetivo(papeis: readonly AcessoDoPapel[], ajustes: readonly AjusteAcesso[] = []): { permissoes: Set<string>; paginasNegadas: Set<string> } {
  const permissoes = new Set<string>();
  for (const p of papeis) for (const x of p.permissoes) permissoes.add(x);
  const paginasNegadas = paginasNegadasDosPapeis(papeis);
  if (permissoes.has('*')) return { permissoes, paginasNegadas: new Set() };
  for (const a of ajustes) {
    if (a.tipo === 'permissao') {
      if (a.concede) permissoes.add(a.codigo);
      else permissoes.delete(a.codigo);
    } else if (a.concede) paginasNegadas.delete(a.codigo);
    else paginasNegadas.add(a.codigo);
  }
  return { permissoes, paginasNegadas };
}

/** Acesso para a conta de páginas: permissões (`*` abre tudo) e páginas negadas. */
export interface AcessoParaPaginas {
  permissoes: ReadonlySet<string>;
  paginasNegadas?: ReadonlySet<string> | null;
}

/**
 * Quem tem este acesso abre a página? Dono (`*`): sempre. Senão, cobre o módulo da página (alguma ação dele) e não a tem
 * negada. Sem ação do módulo, nenhuma página dele abre.
 */
export function abrePagina(a: AcessoParaPaginas, pagina: string): boolean {
  if (a.permissoes.has('*')) return true;
  return cobreModulo(a.permissoes, moduloDaPagina(pagina)) && !a.paginasNegadas?.has(pagina);
}

/**
 * Invariante do acesso efetivo (rodada rval1-10): das páginas do `universo` (as abas dos módulos instalados), as que o acesso
 * `depois` abre, o `antes` não abria e o ator não abre. Lista vazia: a mudança não faz ninguém abrir página acima do ator.
 * `antes` null: quem não tinha acesso nenhum (convite, admissão, reativação). Não depende de o ator ter página negada nem de
 * cobrir o módulo: o ator sem a ação do módulo também não abre as páginas dele.
 */
export function paginasNovasAcimaDoAtor(ator: AcessoParaPaginas, antes: AcessoParaPaginas | null, depois: AcessoParaPaginas, universo: Iterable<string>): string[] {
  if (ator.permissoes.has('*')) return [];
  const saida: string[] = [];
  for (const p of universo) if (abrePagina(depois, p) && !(antes && abrePagina(antes, p)) && !abrePagina(ator, p)) saida.push(p);
  return saida.sort();
}

/**
 * Rotas da API que pertencem SÓ a uma página (página desmarcada: 403 `permissao.pagina_negada`). `escrita`: só os métodos
 * que gravam (POST, PATCH, PUT, DELETE); a leitura da rota serve outras telas (seletores, filtros) e segue a permissão do
 * módulo. `todos`: a rota inteira (a leitura também é só da página). Rota fora desta lista (servida a várias páginas, ou
 * página sem rota própria) segue só a permissão do módulo: desmarcar a página tira a tela e o menu, não a rota comum.
 * O prefixo casa a rota exata ou com `/` depois (`/pipeline/metas` cobre `/pipeline/metas/:id`). Conferido em
 * `packages/shared/tests/acesso-paginas-val1.test.ts` e `apps/api/tests/val1-rotas-da-pagina.test.ts` (todo prefixo existe nas
 * rotas dos módulos e é de uma página só).
 * Rodada rval1-6 (SEG-M1, varredura): `exceto` tira do prefixo rotas declaradas (exatas) que outra tela também usa (ex.: o
 * aluno conclui a aula na Área de membros; a escrita da aula no editor é só da página Cursos).
 */
export interface RotaDaPagina {
  prefixo: string;
  metodos: 'todos' | 'escrita';
  exceto?: readonly string[];
}
export const ROTAS_DA_PAGINA: Readonly<Record<string, readonly RotaDaPagina[]>> = {
  // CRM: metas (a leitura também só a tela de Metas usa), equipes (a lista serve as metas e os filtros).
  'pipeline.metas': [{ prefixo: '/pipeline/metas', metodos: 'todos' }],
  'pipeline.equipes': [{ prefixo: '/pipeline/equipes', metodos: 'escrita' }],
  // Relatórios: cada relatório é uma página.
  // rval1-6 (varredura): o envio da posição ao cliente pelo Portal só a tela Inadimplência faz.
  'relatorios.inadimplencia': [
    { prefixo: '/relatorios/inadimplencia', metodos: 'todos' },
    { prefixo: '/relatorios/cliente', metodos: 'todos' },
  ],
  'relatorios.dre': [{ prefixo: '/relatorios/dre', metodos: 'todos' }],
  'relatorios.fluxo': [{ prefixo: '/relatorios/fluxo', metodos: 'todos' }],
  'relatorios.extrato': [{ prefixo: '/relatorios/extrato', metodos: 'todos' }],
  'relatorios.centros': [{ prefixo: '/relatorios/centros', metodos: 'todos' }],
  'relatorios.parceiros': [{ prefixo: '/relatorios/parceiros', metodos: 'todos' }],
  'relatorios.mrr': [{ prefixo: '/relatorios/mrr', metodos: 'todos' }],
  'relatorios.margem': [{ prefixo: '/relatorios/margem', metodos: 'todos' }],
  'relatorios.quadro': [{ prefixo: '/relatorios/quadro', metodos: 'todos' }],
  'relatorios.folha': [{ prefixo: '/relatorios/folha', metodos: 'todos' }],
  'relatorios.painel-grupo': [{ prefixo: '/relatorios/painel-grupo', metodos: 'todos' }],
  // Financeiro: conciliação e fluxo de caixa.
  // rval1-6 (SEG-M1): as linhas e a lista dos extratos e a importação (com o modelo CSV) também só a Conciliação usa.
  'financeiro.conciliacao': [
    { prefixo: '/financeiro/conciliacao', metodos: 'todos' },
    { prefixo: '/financeiro/extratos', metodos: 'todos' },
  ],
  'financeiro.fluxo-caixa': [{ prefixo: '/financeiro/fluxo-caixa', metodos: 'todos' }],
  // Cadastros de apoio: só a gravação (a lista serve os seletores de outras telas).
  'conversas.respostas-rapidas': [{ prefixo: '/conversas/respostas-rapidas', metodos: 'escrita' }],
  'conversas.templates': [{ prefixo: '/conversas/templates', metodos: 'escrita' }],
  'conversas.filas': [{ prefixo: '/conversas/filas', metodos: 'escrita' }],
  'ausencias.tipos': [{ prefixo: '/ausencias/tipos', metodos: 'escrita' }],
  'automacoes.feriados': [{ prefixo: '/automacoes/feriados', metodos: 'escrita' }],
  // Marcos: fora do mapa (rval1-5). A tela Projetos também grava marcos (`MarcosDoProjeto`): rota de duas telas segue o módulo.
  'pessoas.departamentos': [{ prefixo: '/pessoas/departamentos', metodos: 'escrita' }],
  // rval1-6 (varredura): os itens de onboarding da função só a tela Funções grava.
  'pessoas.funcoes': [
    { prefixo: '/pessoas/funcoes', metodos: 'escrita' },
    { prefixo: '/pessoas/onboarding-itens', metodos: 'escrita' },
  ],
  'pessoas.habilidades': [{ prefixo: '/pessoas/habilidades', metodos: 'escrita' }],
  // rval1-6 (varredura): módulos e aulas do curso são gravados só no editor da página Cursos; concluir a aula e guardar a
  // posição do vídeo são do aluno (Área de membros) e seguem o módulo.
  'treinamento.cursos': [
    { prefixo: '/treinamento/cursos', metodos: 'escrita' },
    { prefixo: '/treinamento/modulos', metodos: 'escrita' },
    { prefixo: '/treinamento/aulas', metodos: 'escrita', exceto: ['/treinamento/aulas/:id/concluir', '/treinamento/aulas/:id/posicao'] },
  ],
  'treinamento.trilhas': [{ prefixo: '/treinamento/trilhas', metodos: 'escrita' }],
  // rval1-6 (SEG-B1): o reenvio de entrega também é gravação só da tela Webhooks.
  'integracoes.webhooks': [
    { prefixo: '/integracoes/webhooks', metodos: 'escrita' },
    { prefixo: '/integracoes/entregas', metodos: 'escrita' },
  ],
  // rval1-6 (varredura): políticas e relógios (com a leitura e a importação do relógio) só a tela Configuração grava.
  'ponto.configuracao': [
    { prefixo: '/ponto/configuracao', metodos: 'escrita' },
    { prefixo: '/ponto/politicas', metodos: 'escrita' },
    { prefixo: '/ponto/relogios', metodos: 'escrita' },
  ],
};

const ESCRITA = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

/** Página dona da rota (método e caminho declarados, `/pipeline/metas/:id`), ou null quando a rota segue só o módulo. */
export function paginaDaRota(metodo: string, caminho: string): string | null {
  const m = metodo.toUpperCase();
  for (const [pagina, rotas] of Object.entries(ROTAS_DA_PAGINA)) {
    for (const r of rotas) {
      if (caminho !== r.prefixo && !caminho.startsWith(`${r.prefixo}/`)) continue;
      if (r.metodos === 'escrita' && !ESCRITA.has(m)) continue;
      if (r.exceto?.includes(caminho)) continue;
      return pagina;
    }
  }
  return null;
}

/**
 * Ficha de 29/10 (item 1, "Arquivos já exportados de relatório sem acesso"): configuração do workspace em `configuracoes`
 * (chave `exportacoes_sem_acesso`, `{ modo }`). `bloquear` (padrão, também sem chave gravada): a lista de exportações e o
 * download deixam de oferecer o arquivo de relatório cuja página a pessoa não abre hoje ou cujo módulo de origem ela não lê;
 * `manter`: o arquivo segue baixável até expirar (o comportamento de antes). Nada é apagado: a conferência é na hora.
 */
export const CHAVE_EXPORTACOES_SEM_ACESSO = 'exportacoes_sem_acesso';
export const MODOS_EXPORTACOES_SEM_ACESSO = ['bloquear', 'manter'] as const;
export type ModoExportacoesSemAcesso = (typeof MODOS_EXPORTACOES_SEM_ACESSO)[number];
export const modoExportacoesSemAcesso = (valor: unknown): ModoExportacoesSemAcesso =>
  valor && typeof valor === 'object' && (valor as { modo?: unknown }).modo === 'manter' ? 'manter' : 'bloquear';
